// Rate → static room mapping (ETG integration requirements §2.4).
// A live rate and a static `room_groups` entry both carry an `rg_ext` object.
// Ported from the mobile back end's resolver so web and mobile behave the same.

type RgExt = Record<string, unknown>;

type StaticRoomGroup = {
  rg_ext?: RgExt | null;
  images?: string[];
  images_ext?: Array<{ url?: string }>;
  [field: string]: unknown;
};

type ResolvedRoom<G extends StaticRoomGroup> = {
  /** Stable room key: the matched static room's key, else the rate's own. */
  key: string;
  /** Static groups that are this room (identical `rg_ext`); empty = no match. */
  groups: G[];
  /** Photos of those groups only, de-duplicated. Never hotel-level photos. */
  images: string[];
};

// Key order can differ between a rate and a static group, so sort the fields.
function rgExtKey(rgExt: unknown, fallback = ''): string {
  if (rgExt && typeof rgExt === 'object') {
    return `rg_${JSON.stringify(Object.fromEntries(Object.entries(rgExt as RgExt).sort(([a], [b]) => a.localeCompare(b))))}`;
  }
  return `name_${fallback.toLowerCase()}`;
}

function photosOf(group: StaticRoomGroup): string[] {
  const urls = group.images_ext?.length ? group.images_ext.map((image) => image.url) : group.images ?? [];
  return urls.filter((url): url is string => typeof url === 'string' && url.length > 0);
}

/**
 * Order of matching:
 * 1. The group whose `rg_ext` is identical.
 * 2. A group that agrees on every field the rate states (non-zero; a rate leaves
 *    a field at 0 when the supplier doesn't say it). The rate must state `class`
 *    and `capacity`.
 * 3. No match: the room has no photos. Another room's photos are never used.
 */
function resolveRoom<G extends StaticRoomGroup>(rateRgExt: unknown, groups: G[] | undefined, fallbackName = ''): ResolvedRoom<G> {
  const own = rgExtKey(rateRgExt, fallbackName);
  const withExt = (groups ?? []).filter((group) => group?.rg_ext && typeof group.rg_ext === 'object');
  const dedupe = (matched: G[]) => [...new Set(matched.flatMap(photosOf))];

  const identical = withExt.filter((group) => rgExtKey(group.rg_ext) === own);
  if (identical.length) return { key: own, groups: identical, images: dedupe(identical) };

  if (!rateRgExt || typeof rateRgExt !== 'object') return { key: own, groups: [], images: [] };
  const stated = Object.entries(rateRgExt as RgExt).filter(([, value]) => typeof value === 'number' && value !== 0);
  if (!stated.some(([field]) => field === 'class') || !stated.some(([field]) => field === 'capacity')) {
    return { key: own, groups: [], images: [] };
  }
  const agreeing = withExt.find((group) => stated.every(([field, value]) => (group.rg_ext as RgExt)[field] === value));
  if (!agreeing) return { key: own, groups: [], images: [] };
  const key = rgExtKey(agreeing.rg_ext);
  const same = withExt.filter((group) => rgExtKey(group.rg_ext) === key);
  return { key, groups: same, images: dedupe(same) };
}

export { resolveRoom, rgExtKey };
export type { ResolvedRoom, RgExt, StaticRoomGroup };
